import { Subscribable, SubscriberId, Subscription } from '@benzinga/subscribable';
import { QuoteSession, QuoteSessionEvents } from './session';
import { Quote, Security } from './entities';
import { QuoteStore } from './store';
import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';
import { Session } from '@benzinga/session';
import { FieldTracker } from './fieldTracker';

/**
 * Event fired by feed when there is an update for a quote
 *
 * Quote object will contain additional data for price and volume
 *
 * @event
 * @interface QuoteEvent
 */
interface QuoteEvent {
  quote: Quote;
  type: 'quote';
}

export type QuoteFeedEvent = QuoteEvent;

/**
 * Feed for receiving data regarding quotes in real time
 *
 * Subscribe to needed events to receive respectful updates
 *
 * @export
 * @class QuoteFeed
 * @extends {Subscribable<QuoteFeedEvent>}
 */
export class QuoteFeed extends Subscribable<QuoteFeedEvent> {
  protected sessions: QuoteSession;
  protected sessionsSubscription?: Subscription<QuoteSession>;
  protected store: QuoteStore;
  protected quote?: Quote;
  private session: Session;
  private fieldTracker: FieldTracker;

  constructor(sessions: QuoteSession, store: QuoteStore, session: Session) {
    super();
    this.session = session;
    this.sessions = sessions;
    this.store = store;
    this.fieldTracker = new FieldTracker(
      this.onFieldsUpdate,
      session.getManager(QuotesV3FieldsManager).getFieldIds() as Set<keyof Quote>,
    );
  }

  public getQuote = (): Quote | undefined => this.quote;
  public getSecurities = (): Security => this.sessions.getSecurity();
  public getFields = (): Set<keyof Quote> => this.sessions.getFields();

  protected socketCallback = (event: QuoteSessionEvents): void => {
    if (event.type == 'quote') {
      if (event.quote.symbol && !event?.quote?.clearFieldNumbers?.length && Object.keys(event.quote).length === 2) {
        // Prevent to send update if event data is empty
        return;
      }
      const quoteFields = this.session.getManager(QuotesV3FieldsManager);
      this.quote = {
        ...this.quote,
        ...event.quote,
        clearFieldNumbers: undefined,
        // priceFormatted: event.quote.price ? formatPrice(event.quote.price) : '',
        ...event.quote.clearFieldNumbers?.reduce(
          (acc, fieldId) => ({ ...acc, [quoteFields.indexToField(fieldId) ?? '']: undefined }),
          {},
        ),
      };
      this.dispatch({ quote: this.quote as Quote, type: 'quote' });
    }
  };

  protected override onSubscribe(id: SubscriberId, args: Set<keyof Quote>): void {
    this.fieldTracker.onSubscribe(id, args);
  }

  protected override onUpdate(id: SubscriberId, args: Set<keyof Quote>): void {
    this.fieldTracker.onUpdate(id, args);
  }

  protected override onUnsubscribe(id: SubscriberId): void {
    this.fieldTracker.onUnsubscribe(id);
  }

  private onFieldsUpdate = (fields: Set<keyof Quote>): void => {
    this.sessions.updateFields(fields);
  };
}

export class QuoteFeedExtended extends QuoteFeed {
  protected override onFirstSubscription(): void {
    this.sessionsSubscription = this.sessions.subscribe(this.socketCallback);
    this.store.addActiveQuoteFeed(this);
  }

  protected override onZeroSubscriptions(): void {
    this.sessionsSubscription?.unsubscribe();
    this.sessionsSubscription = undefined;
    this.store.removeActiveQuoteFeed(this);
    this.store.removeQuoteFeed(this.getSecurities());
  }
}
